import { TaskFilterOptions, TaskType } from '@griply/types';
import { chunkArray } from '@griply/utils';

export type FilteredTaskQueryConfig = {
  ids?: string[];
  type?: string;
  uncompletedOnly?: boolean;
  showCompleted?: boolean;
  goals?: string[];
  lifeAreas?: string[];
  noLifeArea?: boolean;
  noGoal?: boolean;
  deadlineStartDate?: Date;
  deadlineEndDate?: Date;
  deadlineDeadlineStartDate?: Date;
  deadlineDeadlineEndDate?: Date;
  completedStartDate?: Date;
  completedEndDate?: Date;
  tasksOnly?: boolean;
};

export const buildFilteredTaskQueryConfigs = (
  filterOptions: TaskFilterOptions[],
): FilteredTaskQueryConfig[] => {
  const allConfigs: FilteredTaskQueryConfig[] = [];

  filterOptions.forEach((filterSet) => {
    // Skip if types filter excludes TaskType.Task
    if (filterSet.types?.length && !filterSet.types.includes(TaskType.Task)) {
      return;
    }

    // Build base config with query-expressible fields.
    //
    // noDeadline / hasDeadline / noDeadlineDeadline / hasDeadlineDeadline are
    // intentionally NOT pushed down to Firestore: `where('field', '==', null)`
    // fails to match documents where the field is missing entirely (Firestore
    // distinguishes "absent" from "null"), which silently drops legitimate
    // tasks created before those fields were added to the schema. They are
    // applied client-side by `filterTasksClientSide` instead.
    const baseConfig: FilteredTaskQueryConfig = {
      showCompleted: filterSet.showCompleted,
      deadlineStartDate: filterSet.deadlineStartDate,
      deadlineEndDate: filterSet.deadlineEndDate,
      deadlineDeadlineStartDate: filterSet.deadlineDeadlineStartDate,
      deadlineDeadlineEndDate: filterSet.deadlineDeadlineEndDate,
      completedStartDate: filterSet.completedStartDate,
      completedEndDate: filterSet.completedEndDate,
      tasksOnly:
        !filterSet.types?.length || filterSet.types.includes(TaskType.Task),
    };

    const { lifeAreas, goals, ids, noLifeArea, noGoal } = filterSet;

    // Skip if ids is set but empty (no tasks match)
    if (ids && !ids.length) {
      return;
    }

    // Determine if splitting is needed
    const splitQueries =
      [goals?.length, lifeAreas?.length, ids?.length].filter(Boolean).length >
        1 ||
      (lifeAreas?.length && noLifeArea) ||
      (goals?.length && noGoal) ||
      (goals?.length ?? 0) > 30 ||
      (lifeAreas?.length ?? 0) > 30 ||
      (ids?.length ?? 0) > 30;

    // No split needed - return single config
    if (!splitQueries) {
      allConfigs.push({
        ...baseConfig,
        lifeAreas,
        goals,
        ids,
        noLifeArea,
        noGoal,
      });
      return;
    }

    // Split queries - generate multiple configs by chunking
    const { tasksOnly, showCompleted, ...rest } = baseConfig;

    // Chunk lifeAreas
    if (lifeAreas?.length) {
      chunkArray(lifeAreas, 30).forEach((chunked) => {
        allConfigs.push({
          ...rest,
          tasksOnly,
          showCompleted,
          lifeAreas: chunked,
        });
      });
    }

    // Add noLifeArea query
    if (noLifeArea) {
      allConfigs.push({
        ...rest,
        tasksOnly,
        showCompleted,
        noLifeArea: true,
      });
    }

    // Chunk goals
    if (goals?.length) {
      chunkArray(goals, 30).forEach((chunked) => {
        allConfigs.push({
          ...rest,
          tasksOnly,
          showCompleted,
          goals: chunked,
        });
      });
    }

    // Add noGoal query
    if (noGoal) {
      allConfigs.push({
        ...rest,
        tasksOnly,
        showCompleted,
        noGoal: true,
      });
    }

    // Chunk ids
    if (ids?.length) {
      chunkArray(ids, 30).forEach((chunked) => {
        allConfigs.push({
          ...rest,
          tasksOnly,
          showCompleted,
          ids: chunked,
        });
      });
    }
  });

  return allConfigs;
};
